import {
  Button,
  Form,
  H5,
  SmallButton,
  InputNumber,
  InputCurrency,
} from "@sellar-io/components";
import { ProductListResponseType } from "@sellar-io/api-v2/types";
import { formatCurrency } from "@sellar-io/common";
import { useState } from "react";
import { Icon, SpinnerLegacy, StockIndicator } from "../../../components";
import { Alert } from "../../../components/alert/Alert";
import { InputContainer } from "@sellar-io/components";
import { apiV2AssertError } from "../../../hooks/api/v2/apiV2Request";
import { useOrderItemCreate } from "../../../hooks/api/v2/orderItem/useOrderItemCreate";
import { useBuyerProduct } from "../../../hooks/api/v2/product/useBuyerProduct";
import { usePriceFormat } from "../../../hooks/usePriceFormat";
import { useParamAsNumber } from "../../../hooks/useParamAsNumber";
import { ORDER_EDIT } from "../../../constants/routes";
import { OrderEditItemInsufficientStockModal } from "./OrderEditItemInsufficientStockModal";
import { ProductSearchInput } from "./ProductSearchInput";

export const OrderEditItemAdd = ({
  onSuccess,
  retailerId,
}: {
  onSuccess: () => void;
  retailerId: number;
}) => {
  const orderId = useParamAsNumber(ORDER_EDIT, "orderId");

  const [isAddingItem, setIsAddingItem] = useState(false);

  const [quantity, setQuantity] = useState(1);
  const [stockDialogActive, setStockDialogActive] = useState<boolean>(false);
  const [product, setProduct] = useState<ProductListResponseType[number]>();

  const orderItemCreate = useOrderItemCreate();

  const productQuery = useBuyerProduct(product?.id ?? 0, {
    enabled: !!product && !!retailerId,
    RetailerId: retailerId,
  });

  const priceFormat = usePriceFormat(
    productQuery.data?.ProductPriceTier?.price,
  );

  if (orderItemCreate.isPending) return <SpinnerLegacy />;

  if (!isAddingItem) {
    return (
      <div>
        <Button
          className={"w-min cursor-pointer"}
          color={"secondary"}
          onClick={() => setIsAddingItem(true)}
        >
          <Icon icon={"PLUS"} /> Add item
        </Button>
      </div>
    );
  }

  const createOrderItem = (product: ProductListResponseType[number]) => {
    if (priceFormat.apiFormat === null) {
      throw new Error("Cannot find price");
    }

    orderItemCreate.mutate(
      {
        ProductId: product.id,
        OrderId: orderId,
        quantity,
        price: priceFormat.apiFormat,
      },
      {
        onSuccess: () => {
          setIsAddingItem(false);
          setProduct(undefined);
          onSuccess();
        },
      },
    );
  };

  return (
    <div>
      <Form
        onSubmit={() => {
          if (!product)
            throw new Error(
              "A product must be set to create a new order item.",
            );

          if (quantity > product.availableStock && !product.canBackorder) {
            return setStockDialogActive(true);
          }

          createOrderItem(product);
          priceFormat.setApiFormat(0);
        }}
        className={"flex flex-col gap-4"}
      >
        {orderItemCreate.isPending ? (
          <SpinnerLegacy />
        ) : (
          <>
            {orderItemCreate.isError && (
              <Alert
                type={"error"}
                message={
                  apiV2AssertError(orderItemCreate.error)?.response?.data
                    .message ?? "An unknown error occurred"
                }
              />
            )}
            <div className={"flex flex-wrap items-center gap-x-4 gap-y-2"}>
              <div className={"w-[4rem] space-y-2"}>
                <H5>Qty</H5>
                <InputContainer>
                  <InputNumber
                    min={1}
                    value={quantity}
                    onChange={(e) => setQuantity(parseInt(e.target.value))}
                  />
                </InputContainer>
              </div>

              <div className={"min-w-[200px] flex-1 space-y-2"}>
                <H5>Product item name</H5>
                {product ? (
                  <div className={"flex items-center justify-between"}>
                    {product.name}
                    <div className={"flex items-center gap-2"}>
                      {productQuery.data && (
                        <StockIndicator
                          stock={productQuery.data.availableStock}
                          type={"short"}
                        />
                      )}
                      <Button
                        size={"small"}
                        color={"secondary"}
                        onClick={() => {
                          setProduct(undefined);
                          priceFormat.setApiFormat(0);
                        }}
                      >
                        <Icon icon={"CLOSE"} />
                      </Button>
                    </div>
                    <OrderEditItemInsufficientStockModal
                      product={product}
                      onConfirm={() => {
                        setStockDialogActive(false);
                        createOrderItem(product);
                      }}
                      onDismiss={() => setStockDialogActive(false)}
                      quantity={quantity}
                      active={stockDialogActive}
                    />
                  </div>
                ) : (
                  <ProductSearchInput
                    required={true}
                    onChange={(product) => setProduct(product)}
                    placeholder={"Start typing a product name..."}
                  />
                )}
              </div>

              <div className={"w-[8rem] space-y-2"}>
                <H5>Price (per unit)</H5>
                <InputContainer>
                  <InputCurrency
                    showPrefix
                    allowNegative
                    required={true}
                    value={priceFormat.inputFormat}
                    onChange={(e) => priceFormat.setInputFormat(e.target.value)}
                  />
                </InputContainer>
              </div>
              <div className={"space-y-2"}>
                <H5>Line price</H5>
                <div className={"flex h-[46px] items-center"}>
                  {formatCurrency(
                    priceFormat.apiFormat
                      ? priceFormat.apiFormat * quantity
                      : 0,
                  )}
                </div>
              </div>
            </div>
            <div className={"items-right flex justify-end"}>
              <SmallButton
                onClick={() => setIsAddingItem(false)}
                color={"transparent"}
              >
                Cancel
              </SmallButton>
              <SmallButton type={"submit"} disabled={!product}>
                Add
              </SmallButton>
            </div>
          </>
        )}
      </Form>
    </div>
  );
};
